import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { ZodError } from "zod";
import { formatZodError } from "@/utils/formatZodError";

type BoundaryState = {
  error: Error | null;
  message: string | null;
};

type Props = {
  children: ReactNode;
};

export class ZodErrorBoundary extends Component<Props, BoundaryState> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, message: null };
  }

  static getDerivedStateFromError(error: unknown): BoundaryState {
    if (error instanceof ZodError) {
      return {
        error,
        message: formatZodError(error),
      };
    }

    if (error instanceof Error) {
      return {
        error,
        message: error.message,
      };
    }

    return {
      error: new Error("Unknown error"),
      message: "Une erreur inattendue est survenue.",
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ZodErrorBoundary]", error, errorInfo.componentStack);
  }

  private handleReset = () => {
    this.setState({ error: null, message: null });
  };

  render() {
    if (this.state.message) {
      return (
        <Box sx={{ p: 3, maxWidth: 720, mx: "auto" }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {this.state.error instanceof ZodError
                ? "Données invalides"
                : "Une erreur est survenue"}
            </Typography>
            <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", m: 0 }}>
              {this.state.message}
            </Typography>
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" onClick={this.handleReset}>
              Réessayer
            </Button>
            <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
              Recharger la page
            </Button>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}
